import { Link } from 'react-router-dom'
import { motion } from 'motion/react'
import { Button } from '@/components/ui'
import { fugueLabProjects } from '@/data/portfolio'
import { PortfolioCard } from '@/components/PortfolioCard'
import { useSEO, createServiceSchema } from '@/hooks/useSEO'
import { useScrollReveal, useStaggeredGrid, useHeroReveal } from '@gjl/motion/css'
import { generateBreadcrumbs, breadcrumbLabels } from '@/components/Breadcrumbs'
import { Check, Music, Zap, Smartphone, Pencil, Target, Users } from 'lucide-react'

export default function WebsitesPage() {
  const breadcrumbs = generateBreadcrumbs('/services/websites', breadcrumbLabels)

  useSEO({
    title: 'Website Design & Development for Classical Music Organizations',
    description:
      'Modern, fast, mobile-optimized websites for festivals, ensembles, opera companies, choral organizations, and sacred music programs.',
    url: '/services/websites',
    canonical: '/services/websites',
    breadcrumbs,
    schema: createServiceSchema({
      name: 'Website Design & Development',
      description:
        'Custom websites for classical music organizations including festivals, ensembles, and choirs. Modern, fast, and mobile-optimized.',
      url: '/services/websites',
    }),
  })

  // Show all Fugue Lab projects
  const projects = fugueLabProjects

  // Animation refs
  const heroRef = useHeroReveal<HTMLDivElement>(0.2)
  const problemsRef = useStaggeredGrid<HTMLDivElement>('.problem-card', { stagger: 0.08 })
  const packagesRef = useStaggeredGrid<HTMLDivElement>('.package-card', { stagger: 0.12 })
  const portfolioRef = useStaggeredGrid<HTMLDivElement>('.portfolio-item', { stagger: 0.1 })
  const benefitsRef = useStaggeredGrid<HTMLDivElement>('.benefit-card', { stagger: 0.1 })
  const faqRef = useScrollReveal<HTMLDivElement>('.faq-item', { stagger: 0.08 })
  const ctaRef = useScrollReveal<HTMLDivElement>('.cta-content')

  return (
    <div className="mx-auto max-w-6xl space-y-24 px-4 py-16">
      {/* Hero Section */}
      <section ref={heroRef} className="mx-auto max-w-4xl space-y-6 text-center">
        <h1 className="text-fluid-h1">Modern Websites for Classical Music Organizations</h1>
        <p className="text-fluid-h4 text-muted-foreground mx-auto max-w-3xl">
          Your organization creates transformative musical experiences. But if your website is outdated, slow, or
          confusing, you&apos;re losing audiences, applicants, and donors before they ever engage.
        </p>
        <p className="text-fluid-body text-muted mx-auto max-w-2xl">
          We build modern, fast, mobile-optimized websites for festivals, chamber ensembles, opera companies, choral
          organizations, and sacred music programs—websites that reflect the quality of your artistry and convert
          visitors into engaged participants.
        </p>
        <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
          <a href="https://calendly.com/garrett-garrettjohnlaw/30min" target="_blank" rel="noopener noreferrer">
            <motion.div
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              transition={{ type: 'spring', stiffness: 400, damping: 25 }}
            >
              <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                Book a Free Call →
              </Button>
            </motion.div>
          </a>
        </div>
        <div className="text-muted-foreground flex flex-col gap-2 pt-2 text-sm">
          <div className="flex items-center justify-center gap-2">
            <Check className="text-primary h-4 w-4" strokeWidth={2.5} />
            <span>15-minute strategy call, no obligation</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Check className="text-primary h-4 w-4" strokeWidth={2.5} />
            <span>Built by a working musician & software engineer</span>
          </div>
          <div className="flex items-center justify-center gap-2">
            <Check className="text-primary h-4 w-4" strokeWidth={2.5} />
            <span>6-8 week timeline from kickoff to launch</span>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Is Your Website Costing You Audiences, Applicants & Donors?</h2>
        <div ref={problemsRef} className="grid gap-6 md:grid-cols-2">
          {[
            {
              problem: 'Outdated Design',
              description:
                'Your website looks like it was built in 2010. Potential audiences question whether your organization is still active.',
            },
            {
              problem: 'Broken on Mobile',
              description:
                "Most visitors browse on phones. If your site doesn't work on mobile, they'll move on to another organization.",
            },
            {
              problem: 'Slow Load Times',
              description:
                'Your site takes 10+ seconds to load. Studies show 53% of visitors abandon sites that take longer than 3 seconds.',
            },
            {
              problem: 'Confusing Navigation',
              description:
                "Visitors can't find your concert calendar, ticket information, or donation page. They give up and leave.",
            },
            {
              problem: 'Impossible to Update',
              description:
                "You need to hire a developer every time you want to update your concert schedule or artist bios. It's expensive and slow.",
            },
            {
              problem: 'No Clear Call to Action',
              description:
                "There's no obvious path for visitors to buy tickets, donate, or sign up. Interested people don't know what to do next.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="problem-card border-border/50 from-destructive/5 hover:border-destructive/30 space-y-2 rounded-xl border bg-gradient-to-b to-transparent p-6 transition-all duration-300"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-foreground text-lg font-semibold">{item.problem}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>
        <p className="text-muted-foreground mx-auto max-w-2xl pt-4 text-center italic">
          Sound familiar? You&apos;re not alone. Most organization websites were built years ago and haven&apos;t been
          updated to meet modern standards—or audience expectations.
        </p>
      </section>

      {/* Solution Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <div className="space-y-4 text-center">
          <h2 className="text-fluid-h2">The Better Way: A Modern Website That Converts</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            We build modern, fast, beautiful websites specifically for classical music organizations. Sites that look
            professional, work flawlessly on mobile, and make it easy for audiences to engage—whether buying tickets,
            applying to programs, or supporting your mission.
          </p>
        </div>

        <div ref={packagesRef} className="bg-primary/5 border-primary/20 space-y-6 rounded-lg border p-8">
          <h3 className="text-center text-xl font-semibold">Website Packages</h3>
          <p className="text-muted-foreground text-center">
            Pricing tailored to your needs—from individual musicians to large organizations.
          </p>

          <div className="mt-6 grid gap-6 md:grid-cols-3">
            {/* Individual Package */}
            <motion.div
              className="package-card border-border space-y-4 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div>
                <h4 className="text-lg font-semibold">Individual Musicians</h4>
                <p className="text-primary mt-2 text-2xl font-bold">$2,500 - $4,500</p>
                <p className="text-muted-foreground mt-2 text-sm">Solo performers, private teachers, freelancers</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>3-7 page website</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Media gallery</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Event calendar</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Mobile-responsive</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Basic SEO</span>
                </li>
              </ul>
              <p className="text-muted-foreground pt-2 text-xs">Timeline: 2-3 weeks</p>
            </motion.div>

            {/* Small Organization Package */}
            <motion.div
              className="package-card border-primary relative space-y-4 rounded-lg border-2 p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 transform">
                <span className="bg-primary text-primary-foreground rounded-full px-3 py-1 text-xs font-semibold">
                  MOST POPULAR
                </span>
              </div>
              <div>
                <h4 className="text-lg font-semibold">Small Organizations</h4>
                <p className="text-primary mt-2 text-2xl font-bold">$3,000 - $6,000</p>
                <p className="text-muted-foreground mt-2 text-sm">
                  Chamber groups, small festivals, community choruses
                </p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>5-12 page website</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Custom design</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Ticketing integration</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Donation system</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>CMS training</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Email signup forms</span>
                </li>
              </ul>
              <p className="text-muted-foreground pt-2 text-xs">Timeline: 4-6 weeks</p>
            </motion.div>

            {/* Medium-Large Organization Package */}
            <motion.div
              className="package-card border-border space-y-4 rounded-lg border p-6"
              whileHover={{ y: -4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <div>
                <h4 className="text-lg font-semibold">Medium-Large Orgs</h4>
                <p className="text-primary mt-2 text-2xl font-bold">$6,000 - $12,000</p>
                <p className="text-muted-foreground mt-2 text-sm">Established festivals, opera companies, orchestras</p>
              </div>
              <ul className="space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>10-25+ page website</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Fully custom design</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Integrated ticketing system</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Donor management</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Member portals</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Application systems</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="text-primary mt-0.5 h-4 w-4 flex-shrink-0" strokeWidth={2.5} />
                  <span>Advanced analytics</span>
                </li>
              </ul>
              <p className="text-muted-foreground pt-2 text-xs">Timeline: 6-10 weeks</p>
            </motion.div>
          </div>

          <div className="border-border mt-6 border-t pt-6">
            <p className="text-muted-foreground text-center text-sm">
              All packages include mobile optimization, SSL security, basic SEO, and CMS training.
              <br />
              Monthly maintenance available starting at $99/month.
            </p>
          </div>
        </div>
      </section>

      {/* Portfolio Section */}
      {projects.length > 0 && (
        <section className="space-y-8">
          <div className="space-y-4 text-center">
            <h2 className="text-fluid-h2">Websites We&apos;ve Built</h2>
            <p className="text-fluid-body text-muted mx-auto max-w-2xl">
              See how we&apos;ve helped classical music organizations modernize their digital presence and grow their
              audiences.
            </p>
          </div>
          <div ref={portfolioRef} className="columns-1 gap-8 space-y-8 md:columns-2 lg:columns-3">
            {projects.map((project) => (
              <div key={project.slug} className="portfolio-item">
                <PortfolioCard {...project} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Benefits Section */}
      <section className="mx-auto max-w-4xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Why Classical Music Organizations Choose Us</h2>
        <div ref={benefitsRef} className="grid gap-6 md:grid-cols-3">
          {[
            {
              icon: Music,
              title: 'Built by Musicians',
              description:
                "We're not generic web developers. Our founder is a professional organist and church music director who understands the classical music world from the inside.",
            },
            {
              icon: Zap,
              title: 'Fast & Modern',
              description:
                'Built on Next.js—the same technology used by Hulu, Netflix, and TikTok. Your site will be lightning-fast and future-proof.',
            },
            {
              icon: Smartphone,
              title: 'Mobile-First Design',
              description:
                'Modern audiences browse on phones. We design for mobile first, then adapt for desktop—ensuring a seamless experience everywhere.',
            },
            {
              icon: Pencil,
              title: 'Easy to Update',
              description:
                'Update your concert calendar, artist bios, and program information yourself through a simple CMS. No developer needed for routine content changes.',
            },
            {
              icon: Target,
              title: 'Conversion-Focused',
              description:
                'Every page is designed with clear goals: get audiences to buy tickets, donate, apply, or subscribe. Clear CTAs, intuitive navigation, and obvious next steps.',
            },
            {
              icon: Users,
              title: 'White-Glove Service',
              description:
                "You'll work directly with Garrett (founder). No offshore teams, no project managers who don't understand music.",
            },
          ].map((item, idx) => {
            const Icon = item.icon
            return (
              <motion.div
                key={idx}
                className="benefit-card group border-border/50 from-primary/5 hover:border-primary/30 space-y-4 rounded-2xl border bg-gradient-to-b to-transparent p-6 text-center transition-all duration-300 hover:shadow-md"
                whileHover={{ y: -4 }}
                transition={{ type: 'spring', stiffness: 300, damping: 20 }}
              >
                <motion.div
                  className="bg-primary/10 text-primary mx-auto inline-flex h-12 w-12 items-center justify-center rounded-xl"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <Icon className="h-6 w-6" />
                </motion.div>
                <h3 className="text-lg font-semibold">{item.title}</h3>
                <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
              </motion.div>
            )
          })}
        </div>
      </section>

      {/* FAQ Section */}
      <section className="mx-auto max-w-3xl space-y-8">
        <h2 className="text-fluid-h2 text-center">Frequently Asked Questions</h2>
        <div ref={faqRef} className="space-y-6">
          {[
            {
              question: 'How long does it take to build a website?',
              answer:
                '6-8 weeks from kickoff to launch. We work in sprints: Discovery (2 weeks), Design & Development (3 weeks), Content Migration & Testing (2 weeks), Launch & Training (1 week).',
            },
            {
              question: 'Can we update the website ourselves after launch?',
              answer:
                "Yes! We include CMS (Content Management System) integration so you can update concert calendars, programs, artist bios, and other content yourself—no developer needed. We'll train your team on how to use it during launch week.",
            },
            {
              question: 'Do you work with organizations outside the US?',
              answer:
                "Absolutely. We've worked with organizations internationally. All we need is clear communication (Zoom calls work great across time zones) and content from your team.",
            },
            {
              question: 'What if we need ongoing support after launch?',
              answer:
                'We offer Year-Round Digital Partnership packages ($1,500-$6,000/month) that include ongoing updates, email campaigns, analytics, SEO, and strategy. Many organizations prefer this for year-round support aligned with their concert season.',
            },
            {
              question: 'Can you integrate ticketing or donation systems?',
              answer:
                'Yes. We can integrate with most ticketing platforms (Eventbrite, PatronManager, etc.) and donation systems (Stripe, PayPal, Donorbox). If you need a custom system built, we can handle that as part of our Digital Systems package.',
            },
            {
              question: 'What makes you different from other web designers?',
              answer:
                "We're musicians serving musicians. Our founder is a professional organist and church music director who understands the classical music world. We speak your language and build systems tailored to your needs—not generic websites.",
            },
          ].map((item, idx) => (
            <motion.div
              key={idx}
              className="faq-item border-primary space-y-2 border-l-2 pl-6"
              whileHover={{ x: 4 }}
              transition={{ type: 'spring', stiffness: 300, damping: 20 }}
            >
              <h3 className="text-lg font-semibold">{item.question}</h3>
              <p className="text-muted-foreground">{item.answer}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <section
        ref={ctaRef}
        className="bg-primary/5 border-primary/20 mx-auto max-w-3xl space-y-6 rounded-lg border p-12 text-center"
      >
        <div className="cta-content space-y-6">
          <h2 className="text-fluid-h2">Ready to Modernize Your Website?</h2>
          <p className="text-fluid-body text-muted mx-auto max-w-2xl">
            Schedule a free 15-minute digital audit. We&apos;ll review your current site, identify what&apos;s holding
            you back, and show you exactly how we can help.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 pt-4 sm:flex-row">
            <a href="https://calendly.com/garrett-garrettjohnlaw/30min" target="_blank" rel="noopener noreferrer">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="default" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Book a Free Call →
                </Button>
              </motion.div>
            </a>
            <Link to="/contact">
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                transition={{ type: 'spring', stiffness: 400, damping: 25 }}
              >
                <Button variant="outline" size="lg" className="cursor-pointer px-8 py-6 text-lg">
                  Or Send Us a Message
                </Button>
              </motion.div>
            </Link>
          </div>
          <p className="text-muted-foreground text-sm">
            No sales pitch. Just honest feedback on your website and clear next steps if you want our help.
          </p>
        </div>
      </section>
    </div>
  )
}
